import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { notify } from 'react-notify-toast';
import moment from 'moment';
import 'moment/locale/es';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faMoneyBillWave,
  faCalendarAlt,
  faSearch,
  faTimes,
  faCopy,
  faCheck,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faUndo,
  faTimesCircle,
  faUser,
  faEnvelope,
  faPhone,
  faSyncAlt,
  faCode,
  faGraduationCap,
  faChevronLeft,
  faChevronRight,
  faLayerGroup,
  faShieldAlt,
  faTags,
  faHashtag,
} from '@fortawesome/free-solid-svg-icons';

import clientAxios from '../../clients/clientAxios';
import LoadingScreen from '../../Layout/LoadingScreen';
import ModalForRefund from './ModalForRefund';
import ModalDetail from './ModalDetail';
import './paymentMp.css';

const ALL_PAYMENTS = 'Todos los pagos';
const INSTALLMENTS = 'Cuotas';
const ENROLLMENTS = 'Inscripciones';
const ON_DEMAND_COURSES = 'Cursos on demand';
const PAYMENT_LINKS = 'Links de pago';
const ALL_STATES = 'Todos';
const ITEMS_PER_PAGE = 20;
const FORMAT_DATE = 'YYYY-MM-DD';
const DATE_FORMAT_FOR_QUERY = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

const PAYMENT_STATUS_OPTIONS = {
  Todos: ALL_STATES,
  Aprobado: 'approved',
  Pendiente: 'pending',
  Autorizado: 'authorized',
  'En proceso': 'in_process',
  'En mediación': 'in_mediation',
  Rechazado: 'rejected',
  Cancelado: 'cancelled',
  Reembolsado: 'refunded',
  'Devolución de cargo': 'charged_back',
};

const PAYMENT_REFERENCE_MAP = {
  [ALL_PAYMENTS]: 'ALLPAYMENTS',
  [INSTALLMENTS]: 'INSTALLMENTS',
  [ENROLLMENTS]: 'ENROLLMENTS',
  [ON_DEMAND_COURSES]: 'ONDEMANDCOURSES',
  [PAYMENT_LINKS]: 'PAYMENTLINKS',
};

const PaymentsMp = () => {
  const history = useHistory();

  const [paymentsDataMp, setPaymentsDataMp] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState(ALL_PAYMENTS);
  const [paymentStatus, setPaymentStatus] = useState(ALL_STATES);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState(ALL_STATES);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalRefundOpen, setIsModalRefundOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [dataForModal, setDataForModal] = useState({
    email: '',
    numberOperation: '',
  });
  const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
  const [allDataOperation, setAllDataOperation] = useState(null);
  const [loadingStudentDetails, setLoadingStudentDetails] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const [initialDate, setInitialDate] = useState(
    moment().startOf('month').format(FORMAT_DATE)
  );
  const [endDate, setEndDate] = useState(
    moment().startOf('day').format(FORMAT_DATE)
  );
  const [searchValue, setSearchValue] = useState('');

  const getPaymentsMp = useCallback(
    async (customType, customStatus, customStart, customEnd, customSearch) => {
      setIsLoading(true);
      try {
        const activeType = customType !== undefined ? customType : paymentType;
        const activeStatus = customStatus !== undefined ? customStatus : paymentStatus;
        const activeStart = customStart !== undefined ? customStart : initialDate;
        const activeEnd = customEnd !== undefined ? customEnd : endDate;
        const activeSearch = customSearch !== undefined ? customSearch : searchValue;

        const initialDateForQuery = moment(activeStart).format(DATE_FORMAT_FOR_QUERY);
        const endDateForQuery = moment(activeEnd).endOf('day').format(DATE_FORMAT_FOR_QUERY);
        const referenceKey = PAYMENT_REFERENCE_MAP[activeType] || 'ALLPAYMENTS';

        const { data } = await clientAxios.get(
          `/bills/payments/mercadopago?external_reference=${referenceKey}&begin_date=${initialDateForQuery}&end_date=${endDateForQuery}&limit=${ITEMS_PER_PAGE}`
        );

        let totalData = Array.isArray(data)
          ? data
              .flatMap((item) => item?.body?.results || [])
              .sort(
                (firstItem, secondItem) =>
                  new Date(secondItem.date_created) - new Date(firstItem.date_created)
              )
          : data?.body?.results || [];

        if (activeStatus !== ALL_STATES) {
          totalData = totalData.filter(
            (paymentItem) => paymentItem.status === activeStatus
          );
        }

        setPaymentsDataMp(totalData);

        if (activeSearch && activeSearch.trim() !== '') {
          const cleanSearch = activeSearch.trim().toLowerCase();
          const filtered = totalData.filter((paymentItem) => {
            const operationId = String(paymentItem?.id || '');
            const email = (
              paymentItem?.metadata?.email ||
              paymentItem?.payer?.email ||
              ''
            ).toLowerCase();
            const fullName = `${
              paymentItem?.metadata?.display_name ||
              paymentItem?.metadata?.firstname ||
              paymentItem?.metadata?.first_name ||
              ''
            } ${
              paymentItem?.metadata?.lastname ||
              paymentItem?.metadata?.last_name ||
              ''
            }`.toLowerCase();

            return (
              operationId.includes(cleanSearch) ||
              email.includes(cleanSearch) ||
              fullName.includes(cleanSearch)
            );
          });
          setSearchResults(filtered);
        } else {
          setSearchResults([]);
        }

        setCurrentPage(1);
      } catch (error) {
        notify.show(
          'Ha ocurrido un error al intentar consultar las transacciones de Mercado Pago.',
          'error'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [paymentType, paymentStatus, initialDate, endDate, searchValue]
  );

  useEffect(() => {
    getPaymentsMp();
  }, [getPaymentsMp]);

  const activeDataset = useMemo(() => {
    return searchResults.length > 0 ? searchResults : paymentsDataMp;
  }, [searchResults, paymentsDataMp]);

  const totalPagesCount = Math.max(1, Math.ceil(activeDataset.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPayments = activeDataset.slice(startIndex, endIndex);

  const stats = useMemo(() => {
    const rawList = paymentsDataMp || [];
    const totalTransactions = rawList.length;
    const approvedPayments = rawList.filter(
      (paymentItem) => paymentItem.status === 'approved'
    );
    const approvedCount = approvedPayments.length;
    const totalApprovedAmount = approvedPayments.reduce(
      (accumulator, paymentItem) =>
        accumulator +
        Number(
          paymentItem?.transaction_details?.total_paid_amount ||
            paymentItem?.transaction_amount ||
            0
        ),
      0
    );
    const pendingCount = rawList.filter((paymentItem) =>
      ['pending', 'in_process', 'authorized'].includes(paymentItem.status)
    ).length;
    const refundedCount = rawList.filter(
      (paymentItem) => paymentItem.status === 'refunded'
    ).length;

    return {
      totalTransactions,
      totalApprovedAmount,
      approvedCount,
      pendingCount,
      refundedCount,
    };
  }, [paymentsDataMp]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPagesCount) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePaymentTypeChange = (newType) => {
    setPaymentType(newType);
    getPaymentsMp(newType, paymentStatus, initialDate, endDate, searchValue);
  };

  const handlePaymentStatusChange = (statusLabel) => {
    setSelectedStatusLabel(statusLabel);
    const mappedStatus = PAYMENT_STATUS_OPTIONS[statusLabel] || ALL_STATES;
    setPaymentStatus(mappedStatus);
    getPaymentsMp(paymentType, mappedStatus, initialDate, endDate, searchValue);
  };

  const handleSearchSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    getPaymentsMp(paymentType, paymentStatus, initialDate, endDate, searchValue);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    getPaymentsMp(paymentType, paymentStatus, initialDate, endDate, '');
  };

  const handleResetAllFilters = () => {
    const startOfMonth = moment().startOf('month').format(FORMAT_DATE);
    const today = moment().startOf('day').format(FORMAT_DATE);
    setPaymentType(ALL_PAYMENTS);
    setPaymentStatus(ALL_STATES);
    setSelectedStatusLabel(ALL_STATES);
    setInitialDate(startOfMonth);
    setEndDate(today);
    setSearchValue('');
    setSearchResults([]);
    getPaymentsMp(ALL_PAYMENTS, ALL_STATES, startOfMonth, today, '');
  };

  const handleCopyOperationId = (operationId) => {
    if (!operationId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(operationId));
      }
    } catch (err) {}
    setCopiedId(operationId);
    notify.show(`Operación #${operationId} copiada al portapapeles`, 'success', 2000);
    setTimeout(() => {
      setCopiedId(null);
    }, 2500);
  };

  const onModalRefundOpen = (email, numberOperation) => {
    setDataForModal({ email, numberOperation });
    setIsModalRefundOpen(true);
  };

  const onModalDetailOpen = (paymentData) => {
    const objectToJson = JSON.stringify(paymentData, null, 2);
    setAllDataOperation(objectToJson);
    setIsModalDetailOpen(true);
  };

  const handleVerDetallesAlumno = async (paymentItem) => {
    const paymentId = paymentItem.id;
    setLoadingStudentDetails((previousState) => ({
      ...previousState,
      [paymentId]: true,
    }));

    try {
      const email = paymentItem.metadata?.email || paymentItem.payer?.email;
      if (!email) {
        notify.show('No se encontró email en el registro de pago', 'error');
        return;
      }

      const { data } = await clientAxios.get('/students/payment-info', {
        params: { email },
      });

      if (data?.exists) {
        history.push(`/students/details/${data.studentId}`);
      } else if (data?.pendingData) {
        const formattedData = JSON.stringify(data.pendingData, null, 2);
        setAllDataOperation(formattedData);
        setIsModalDetailOpen(true);
      } else {
        notify.show(
          'No se encontraron datos registrados del alumno con este email',
          'warning'
        );
      }
    } catch (error) {
      notify.show('Error al obtener datos del alumno', 'error');
    } finally {
      setLoadingStudentDetails((previousState) => ({
        ...previousState,
        [paymentId]: false,
      }));
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className='mp-status-pill mp-status-approved'>
            <FontAwesomeIcon icon={faCheckCircle} />
            Aprobado
          </span>
        );
      case 'pending':
        return (
          <span className='mp-status-pill mp-status-pending'>
            <FontAwesomeIcon icon={faClock} />
            Pendiente
          </span>
        );
      case 'in_process':
        return (
          <span className='mp-status-pill mp-status-in_process'>
            <FontAwesomeIcon icon={faClock} />
            En proceso
          </span>
        );
      case 'authorized':
        return (
          <span className='mp-status-pill mp-status-authorized'>
            <FontAwesomeIcon icon={faShieldAlt} />
            Autorizado
          </span>
        );
      case 'refunded':
        return (
          <span className='mp-status-pill mp-status-refunded'>
            <FontAwesomeIcon icon={faUndo} />
            Reembolsado
          </span>
        );
      case 'rejected':
        return (
          <span className='mp-status-pill mp-status-rejected'>
            <FontAwesomeIcon icon={faTimesCircle} />
            Rechazado
          </span>
        );
      case 'cancelled':
        return (
          <span className='mp-status-pill mp-status-cancelled'>
            <FontAwesomeIcon icon={faTimesCircle} />
            Cancelado
          </span>
        );
      case 'in_mediation':
        return (
          <span className='mp-status-pill mp-status-in_mediation'>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Mediación
          </span>
        );
      case 'charged_back':
        return (
          <span className='mp-status-pill mp-status-charged_back'>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Contracargo
          </span>
        );
      default:
        return (
          <span className='mp-status-pill mp-status-pending'>
            {status || 'Desconocido'}
          </span>
        );
    }
  };

  return (
    <>
      <div className='payments-mp-container'>
        <div className='payments-mp-header'>
          <div className='payments-mp-title-box'>
            <h2>
              <span className='payments-mp-title-icon'>
                <FontAwesomeIcon icon={faCreditCard} />
              </span>
              Pagos de Mercado Pago
            </h2>
            <p>
              Conciliación, auditoría y administración de transacciones online en tiempo real
            </p>
          </div>

          <div className='payments-mp-header-actions'>
            <button
              type='button'
              className='btn-mp-refresh'
              onClick={() => getPaymentsMp()}
              disabled={isLoading}
              title='Actualizar lista'
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={isLoading} />
              Actualizar
            </button>
          </div>
        </div>

        <div className='payments-mp-stats-grid'>
          <div className='mp-stat-card'>
            <div className='mp-stat-icon-wrapper blue'>
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            <div className='mp-stat-info'>
              <span className='mp-stat-value'>{stats.totalTransactions}</span>
              <span className='mp-stat-label'>Total Transacciones</span>
            </div>
          </div>

          <div className='mp-stat-card'>
            <div className='mp-stat-icon-wrapper emerald'>
              <FontAwesomeIcon icon={faMoneyBillWave} />
            </div>
            <div className='mp-stat-info'>
              <span className='mp-stat-value'>
                ${stats.totalApprovedAmount.toLocaleString('es-AR')}
              </span>
              <span className='mp-stat-label'>Total Aprobado</span>
            </div>
          </div>

          <div className='mp-stat-card'>
            <div className='mp-stat-icon-wrapper emerald'>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className='mp-stat-info'>
              <span className='mp-stat-value'>{stats.approvedCount}</span>
              <span className='mp-stat-label'>Pagos Aprobados</span>
            </div>
          </div>

          <div className='mp-stat-card'>
            <div className='mp-stat-icon-wrapper amber'>
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className='mp-stat-info'>
              <span className='mp-stat-value'>{stats.pendingCount}</span>
              <span className='mp-stat-label'>Pendientes / Proceso</span>
            </div>
          </div>

          <div className='mp-stat-card'>
            <div className='mp-stat-icon-wrapper purple'>
              <FontAwesomeIcon icon={faUndo} />
            </div>
            <div className='mp-stat-info'>
              <span className='mp-stat-value'>{stats.refundedCount}</span>
              <span className='mp-stat-label'>Reembolsos</span>
            </div>
          </div>
        </div>

        <div className='payments-mp-filter-card'>
          <div className='mp-filter-type-tabs'>
            {[
              ALL_PAYMENTS,
              INSTALLMENTS,
              ENROLLMENTS,
              ON_DEMAND_COURSES,
              PAYMENT_LINKS,
            ].map((typeItem) => (
              <button
                key={typeItem}
                type='button'
                className={`mp-type-tab-btn ${
                  paymentType === typeItem ? 'active' : ''
                }`}
                onClick={() => handlePaymentTypeChange(typeItem)}
              >
                <FontAwesomeIcon icon={faTags} />
                {typeItem}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearchSubmit}>
            <div className='mp-filter-controls-grid'>
              <div className='mp-filter-field'>
                <label className='mp-filter-label'>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  Desde
                </label>
                <input
                  type='date'
                  value={initialDate}
                  max={moment().format(FORMAT_DATE)}
                  onChange={(event) => setInitialDate(event.target.value)}
                  className='mp-input-custom'
                />
              </div>

              <div className='mp-filter-field'>
                <label className='mp-filter-label'>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  Hasta
                </label>
                <input
                  type='date'
                  value={endDate}
                  max={moment().format(FORMAT_DATE)}
                  onChange={(event) => setEndDate(event.target.value)}
                  className='mp-input-custom'
                />
              </div>

              <div className='mp-filter-field'>
                <label className='mp-filter-label'>
                  <FontAwesomeIcon icon={faShieldAlt} />
                  Estado
                </label>
                <select
                  value={selectedStatusLabel}
                  onChange={(event) =>
                    handlePaymentStatusChange(event.target.value)
                  }
                  className='mp-select-custom'
                >
                  {Object.keys(PAYMENT_STATUS_OPTIONS).map((statusKey) => (
                    <option key={statusKey} value={statusKey}>
                      {statusKey}
                    </option>
                  ))}
                </select>
              </div>

              <div className='mp-filter-field'>
                <label className='mp-filter-label'>
                  <FontAwesomeIcon icon={faSearch} />
                  Buscar (ID, Email, Nombre)
                </label>
                <div className='mp-search-group'>
                  <FontAwesomeIcon icon={faSearch} className='mp-search-icon' />
                  <input
                    type='text'
                    placeholder='N° operación, email o nombre...'
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    className='mp-input-custom mp-search-input'
                  />
                  {searchValue && (
                    <button
                      type='button'
                      className='mp-search-clear'
                      onClick={handleClearSearch}
                      title='Limpiar búsqueda'
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>

              <div className='mp-filter-actions'>
                <button
                  type='submit'
                  className='btn-mp-apply'
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Filtrar
                </button>
                <button
                  type='button'
                  className='btn-mp-reset'
                  onClick={handleResetAllFilters}
                  title='Restablecer filtros'
                >
                  Limpiar
                </button>
              </div>
            </div>
          </form>
        </div>

        {isLoading ? (
          <LoadingScreen />
        ) : (
          <div className='payments-mp-table-card'>
            {currentPayments.length === 0 ? (
              <div className='payments-mp-empty-state'>
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className='mp-empty-icon'
                />
                <h4 className='mp-empty-title'>No se encontraron transacciones</h4>
                <p className='mp-empty-text'>
                  {searchValue
                    ? `No se hallaron resultados para "${searchValue}". Intenta con otro criterio de búsqueda.`
                    : 'No hay pagos de Mercado Pago para el período o filtros seleccionados.'}
                </p>
                <button
                  type='button'
                  className='btn-mp-apply'
                  onClick={handleResetAllFilters}
                >
                  Restablecer filtros
                </button>
              </div>
            ) : (
              <>
                <div className='payments-mp-table-wrapper'>
                  <table className='payments-mp-custom-table'>
                    <thead>
                      <tr>
                        <th>Fecha & Hora</th>
                        <th>N° Operación</th>
                        <th>Alumno / Pagador</th>
                        <th>Monto</th>
                        <th>Estado</th>
                        <th>Concepto / Cuota</th>
                        <th>Medio de Pago</th>
                        <th className='text-center'>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPayments.map((paymentItem, index) => {
                        const payerFullName =
                          `${
                            paymentItem?.metadata?.display_name ||
                            paymentItem?.metadata?.firstname ||
                            paymentItem?.metadata?.first_name ||
                            ''
                          } ${
                            paymentItem?.metadata?.lastname ||
                            paymentItem?.metadata?.last_name ||
                            ''
                          }`.trim() || 'Sin nombre registrado';

                        const payerEmail =
                          paymentItem?.metadata?.email ||
                          paymentItem?.payer?.email ||
                          '-';

                        const payerPhone =
                          paymentItem?.studentPhone ||
                          paymentItem?.payer?.phone?.number ||
                          paymentItem?.metadata?.phone;

                        const paidAmount = Number(
                          paymentItem?.transaction_details?.total_paid_amount ||
                            paymentItem?.transaction_amount ||
                            0
                        );

                        const classroomOrDesc =
                          paymentItem?.metadata?.class_room ||
                          paymentItem?.description ||
                          'Pago Mercado Pago';

                        const installmentNumber =
                          paymentItem?.metadata?.installment ||
                          paymentItem?.installments;

                        const isRefundEligible =
                          paymentItem?.status === 'approved';

                        return (
                          <tr key={paymentItem?.id || index}>
                            <td>
                              <div className='mp-date-cell'>
                                <span className='mp-date-main'>
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                  {moment(paymentItem?.date_created).format(
                                    'DD/MM/YYYY'
                                  )}
                                </span>
                                <span className='mp-date-sub'>
                                  {moment(paymentItem?.date_created).format(
                                    'HH:mm:ss'
                                  )} hs
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className='mp-id-badge'>
                                <FontAwesomeIcon icon={faHashtag} />
                                <span>{paymentItem?.id}</span>
                                <button
                                  type='button'
                                  className='btn-copy-id'
                                  onClick={() =>
                                    handleCopyOperationId(paymentItem?.id)
                                  }
                                  title='Copiar número de operación'
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      copiedId === paymentItem?.id
                                        ? faCheck
                                        : faCopy
                                    }
                                  />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className='mp-payer-cell'>
                                <span className='mp-payer-name' title={payerFullName}>
                                  <FontAwesomeIcon icon={faUser} className='mr-1 text-muted' />
                                  {payerFullName}
                                </span>
                                <span className='mp-payer-email' title={payerEmail}>
                                  <FontAwesomeIcon icon={faEnvelope} className='mr-1' />
                                  {payerEmail}
                                </span>
                                {payerPhone && (
                                  <span className='mp-payer-phone'>
                                    <FontAwesomeIcon icon={faPhone} className='mr-1' />
                                    {payerPhone}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td>
                              <span className='mp-amount-value'>
                                ${paidAmount.toLocaleString('es-AR')}
                              </span>
                            </td>

                            <td>{renderStatusBadge(paymentItem?.status)}</td>

                            <td>
                              <div className='mp-concept-cell'>
                                <span
                                  className='mp-concept-title'
                                  title={classroomOrDesc}
                                >
                                  {classroomOrDesc}
                                </span>
                                {installmentNumber && (
                                  <span className='mp-installment-tag'>
                                    Cuota {installmentNumber}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td>
                              <div className='mp-method-cell'>
                                <span className='mp-method-name'>
                                  {paymentItem?.payment_method_id ||
                                    paymentItem?.payment_type_id ||
                                    '-'}
                                </span>
                                {paymentItem?.card?.last_four_digits ? (
                                  <span className='mp-card-last4'>
                                    <FontAwesomeIcon icon={faCreditCard} />
                                    •••• {paymentItem?.card?.last_four_digits}
                                  </span>
                                ) : (
                                  <span className='text-muted small'>
                                    {paymentItem?.payment_type_id}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td>
                              <div className='mp-actions-cell'>
                                <button
                                  type='button'
                                  className='btn-mp-action btn-mp-action-student'
                                  onClick={() =>
                                    handleVerDetallesAlumno(paymentItem)
                                  }
                                  disabled={
                                    loadingStudentDetails[paymentItem.id]
                                  }
                                >
                                  {loadingStudentDetails[paymentItem.id] ? (
                                    <>
                                      <Spinner
                                        as='span'
                                        animation='border'
                                        size='sm'
                                        role='status'
                                        className='mr-1'
                                      />
                                      Cargando...
                                    </>
                                  ) : (
                                    <>
                                      <FontAwesomeIcon
                                        icon={faGraduationCap}
                                      />
                                      Detalles Alumno
                                    </>
                                  )}
                                </button>

                                {isRefundEligible && (
                                  <button
                                    type='button'
                                    className='btn-mp-action btn-mp-action-refund'
                                    onClick={() =>
                                      onModalRefundOpen(
                                        paymentItem?.metadata?.email ||
                                          paymentItem?.payer?.email,
                                        paymentItem?.id
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon icon={faUndo} />
                                    Reembolso
                                  </button>
                                )}

                                <button
                                  type='button'
                                  className='btn-mp-action btn-mp-action-detail'
                                  onClick={() => onModalDetailOpen(paymentItem)}
                                >
                                  <FontAwesomeIcon icon={faCode} />
                                  Datos en MP
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className='payments-mp-footer'>
                  <div className='payments-mp-summary-text'>
                    Mostrando{' '}
                    <strong>
                      {activeDataset.length === 0 ? 0 : startIndex + 1}
                    </strong>{' '}
                    a{' '}
                    <strong>
                      {Math.min(endIndex, activeDataset.length)}
                    </strong>{' '}
                    de <strong>{activeDataset.length}</strong> transacciones
                  </div>

                  {totalPagesCount > 1 && (
                    <div className='payments-mp-pagination'>
                      <button
                        type='button'
                        className='payments-mp-page-btn'
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        title='Página anterior'
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>

                      {[...Array(totalPagesCount)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          totalPagesCount > 7 &&
                          Math.abs(pageNumber - currentPage) > 2 &&
                          pageNumber !== 1 &&
                          pageNumber !== totalPagesCount
                        ) {
                          if (
                            pageNumber === currentPage - 3 ||
                            pageNumber === currentPage + 3
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className='payments-mp-page-btn'
                                style={{
                                  border: 'none',
                                  cursor: 'default',
                                  background: 'transparent',
                                }}
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        }

                        return (
                          <button
                            key={pageNumber}
                            type='button'
                            className={`payments-mp-page-btn ${
                              currentPage === pageNumber ? 'active' : ''
                            }`}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}

                      <button
                        type='button'
                        className='payments-mp-page-btn'
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPagesCount}
                        title='Página siguiente'
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ModalForRefund
        isModaRefundOpen={isModalRefundOpen}
        setIsModalRefundOpen={setIsModalRefundOpen}
        dataForModal={dataForModal}
        getPaymentsMp={getPaymentsMp}
      />
      <ModalDetail
        isModalDetailOpen={isModalDetailOpen}
        setIsModalDetailOpen={setIsModalDetailOpen}
        allData={allDataOperation}
      />
    </>
  );
};

export default PaymentsMp;
